import { clone } from '@/utils/helpers/'

const DEFAULT_POINTS = 5
const SMS_EMAIL_POINTS = 10

export async function normalizeCampaignData(data) {
  let normalizedDataToClean = clone(data)
  const campaignType = data.type

  let presentation = data.presentation
  let settings = data.settings
  let socialActions = data.socialActions
  let meta = {}

  let normalizedRegister = getNormalizedRegisterData(presentation, settings)
  let normalizedSocialActions = getNormalizedSocialActionsData(presentation, socialActions, campaignType)

  let existingMeta = {
    invite: {
      copy: null,
    },
  }

  // if old property of pageOptions exists, set new meta property to it
  if (!!normalizedDataToClean.presentation?.pageOptions && !!Object.keys(normalizedDataToClean.presentation?.pageOptions).length) {
    existingMeta = {
      ...normalizedDataToClean.presentation.pageOptions,
    }
  }

  // if new property of presentation.meta exists, it is more recent, so reassign new meta property to this instead
  if (!!normalizedDataToClean.presentation?.meta && !!Object.keys(normalizedDataToClean.presentation?.meta).length) {
    existingMeta = {
      ...existingMeta,
      ...normalizedDataToClean.presentation.meta,
    }
  }

  meta = {
    ...existingMeta,
    register: normalizedRegister,
  }

  normalizedDataToClean = {
    ...normalizedDataToClean,
    presentation: {
      ...normalizedDataToClean.presentation,
      meta,
    },
    socialActions: normalizedSocialActions,
  }

  // when we know that data is updating
  // and all working correctly, the below
  // function is where we will delete/remove
  // reference to old properties.
  // For now we are not removing old properties
  // in case we need to debug/troubleshoot/rollback.
  let normalizedData = cleanOldDataProperties(normalizedDataToClean)

  return normalizedData
}

function cleanOldDataProperties(data) {
  let newDataObj = clone(data)
  // delete newDataObj.presentation.headHtml
  // delete newDataObj.presentation.bodyHtml

  return newDataObj
}

function getActionsFromSocialAccount(newAccount, campaignType) {
  let actions = []

  if (!newAccount) {
    return actions
  }

  const newAccountKeys = Object.keys(newAccount)

  if (!!newAccountKeys && newAccountKeys.length > 0) {
    newAccountKeys.forEach((accountKey) => {
      if (
        accountKey != 'name' && accountKey != 'key' // Action Identifiers
        && accountKey != 'additionalInfo' // Contains Privacy Policy
        && accountKey != 'save' // Saving a brand is now manual, so we no longer support this
        && accountKey != 'facebook:messenger' && accountKey != 'snapchat' // Actions no longer supported
      ) {
        let value = newAccount[accountKey]
        let points = DEFAULT_POINTS

        if (accountKey === 'sms' || accountKey === 'email') {
          // If value is actually set to disable, we should skip adding this action.
          if (value === 'disable') {
            return
          }

          if (value === 'enable' && campaignType !== 'opt-in') {
            value = 'enable'
          }
          points = SMS_EMAIL_POINTS
        }

        actions.push({
          key: accountKey,
          value,
          label: null,
          disclaimer: null,
          points,
        })

        // TO DO: Delete the key on second stage
        // delete newAccount[accountKey]
      }
    })
  }

  return actions
}

function getNormalizedSocialAccounts(newAccount, campaignType) {
  // Actions haven't been defined yet
  if (!newAccount.actions) {
    newAccount.actions = getActionsFromSocialAccount(newAccount, campaignType)
  }

  return newAccount
}

function getNormalizedSocialActionsData(presentation, social, campaignType) {
  let normalizedSocialActions = clone(social)
  let normalizedSocialAccounts

  if (!!normalizedSocialActions && normalizedSocialActions.socialAccounts?.length > 0) {
    normalizedSocialAccounts = normalizedSocialActions.socialAccounts.map((account) => {
      return getNormalizedSocialAccounts(clone(account), campaignType)
    })
  }

  normalizedSocialActions = {
    ...normalizedSocialActions,
    socialAccounts: normalizedSocialAccounts,
  }

  normalizedSocialActions = {
    ...normalizedSocialActions,
    meta: {
      title: !!presentation?.metaTitle && (!normalizedSocialActions?.meta || !(normalizedSocialActions.meta && 'title' in normalizedSocialActions.meta)) ? presentation.metaTitle : normalizedSocialActions?.meta?.title || '',
      description: !!presentation?.metaDescription && (!normalizedSocialActions?.meta || !(normalizedSocialActions.meta && 'description' in normalizedSocialActions.meta)) ? presentation.metaDescription : normalizedSocialActions?.meta?.description || '',
    },
  }

  return normalizedSocialActions
}

function getNormalizedRegisterData(presentation, settings) {
  let normalizedRegister = presentation?.meta?.register ? presentation.meta.register : {}

  // The logic for the below reads

  // if the property exists in the old location
  // but it either doesn't exist within the new location (presentation.meta.register.<newLocation>.<newProperty>)
  // OR presentation.meta.register.<newLocation> doesn't exist at all
  // --> this is the first time the normalization has run,
  // set the values to the data that exists in the old location
  // OTHERWISE
  // --> the property exists, hence the normalization has been run previously
  // set the values to the data that exists in the new normalization location

  normalizedRegister = {
    customHtml: {
      headHtml: !!presentation?.headHtml && (!(normalizedRegister?.customHtml) || !(normalizedRegister.customHtml && 'headHtml' in normalizedRegister.customHtml)) ? presentation.headHtml : normalizedRegister?.customHtml?.headHtml,
      bodyHtml: !!presentation?.bodyHtml && (!(normalizedRegister?.customHtml) || !(normalizedRegister.customHtml && 'bodyHtml' in normalizedRegister.customHtml)) ? presentation.bodyHtml : normalizedRegister?.customHtml?.bodyHtml,
    },
    customCta: !!settings?.customCta && (!normalizedRegister || !(normalizedRegister && 'customCta' in normalizedRegister)) ? settings.customCta : normalizedRegister?.customCta,
    customLive: {
      headerText: !!presentation?.liveCustomHeader && (!normalizedRegister?.customLive || !(normalizedRegister.customLive && 'headerText' in normalizedRegister.customLive)) ? presentation.liveCustomHeader : normalizedRegister?.customLive?.headerText,
      bodyText: !!presentation?.liveCustomDescription && (!normalizedRegister?.customLive || !(normalizedRegister.customLive && 'bodyText' in normalizedRegister.customLive)) ? presentation.liveCustomDescription : normalizedRegister?.customLive?.bodyText,
    },
    media: {
      noImageBackground: !!('noImageBackground' in presentation) && (!normalizedRegister?.media || !(normalizedRegister.media && 'noImageBackground' in normalizedRegister.media)) ? presentation.noImageBackground : !!normalizedRegister?.media?.noImageBackground,
      externalVideoMediaType: !!presentation?.externalVideoMediaType && (!normalizedRegister?.media || !(normalizedRegister.media && 'externalVideoMediaType' in normalizedRegister.media)) ? presentation.externalVideoMediaType : normalizedRegister?.media?.externalVideoMediaType,
      externalVideoUrl: !!presentation?.externalVideoUrl && (!normalizedRegister?.media || !(normalizedRegister.media && 'externalVideoUrl' in normalizedRegister.media)) ? presentation.externalVideoUrl : normalizedRegister?.media?.externalVideoUrl,
    },
    noPoints: !!('noPoints' in presentation) && (!normalizedRegister || !(normalizedRegister && 'noPoints' in normalizedRegister)) ? presentation.noPoints : !!normalizedRegister?.noPoints,
    privacyPolicy: {
      companyName: !!settings?.privacyPolicy?.companyName && (!(normalizedRegister?.privacyPolicy) || !(normalizedRegister.privacyPolicy && 'companyName' in normalizedRegister.privacyPolicy)) ? settings.privacyPolicy.companyName : normalizedRegister?.privacyPolicy?.companyName,
      url: !!settings?.privacyPolicy?.url && (!(normalizedRegister?.privacyPolicy) || !(normalizedRegister.privacyPolicy && 'url' in normalizedRegister.privacyPolicy)) ? settings.privacyPolicy.url : normalizedRegister?.privacyPolicy?.url,
    },
    tcpaSettings: {
      companyName: !!settings?.tcpaSettings?.companyName && (!normalizedRegister?.tcpaSettings || !(normalizedRegister.tcpaSettings && 'companyName' in normalizedRegister.tcpaSettings)) ? settings.tcpaSettings.companyName : normalizedRegister?.tcpaSettings?.companyName,
      enabled: !!(settings?.tcpaSettings && 'enabled' in settings.tcpaSettings) && (!normalizedRegister?.tcpaSettings || !(normalizedRegister.tcpaSettings && 'enabled' in normalizedRegister.tcpaSettings)) ? settings.tcpaSettings.enabled : !!normalizedRegister?.tcpaSettings?.enabled,
    },
    customSuccessPage: {
      buttonEnabled: !!('customSuccessButtonEnabled' in presentation) && (!normalizedRegister?.customSuccessPage || !(normalizedRegister.customSuccessPage && 'buttonEnabled' in normalizedRegister.customSuccessPage)) ? presentation.customSuccessButtonEnabled : !!normalizedRegister?.customSuccessPage?.buttonEnabled,
      message: getCustomSuccessMessage(presentation, normalizedRegister),
      buttonText: !!presentation?.customSuccessButtonText && (!normalizedRegister?.customSuccessPage || !(normalizedRegister.customSuccessPage && 'buttonText' in normalizedRegister.customSuccessPage)) ? presentation.customSuccessButtonText : normalizedRegister?.customSuccessPage?.buttonText,
      buttonUrl: !!presentation?.customSuccessButtonUrl && (!normalizedRegister?.customSuccessPage || !(normalizedRegister.customSuccessPage && 'buttonUrl' in normalizedRegister.customSuccessPage)) ? presentation.customSuccessButtonUrl : normalizedRegister?.customSuccessPage?.buttonUrl,
    },
    subscriptionPreferenceSettings: {
      enabled: !!(settings?.subscriptionPreferenceSettings && (!normalizedRegister?.subscriptionPreferenceSettings || (settings.subscriptionPreferenceSettings && 'enabled' in settings.subscriptionPreferenceSettings))) && (!normalizedRegister?.subscriptionPreferenceSettings || !(normalizedRegister.subscriptionPreferenceSettings && 'enabled' in normalizedRegister.subscriptionPreferenceSettings)) ? settings.subscriptionPreferenceSettings.enabled : !!normalizedRegister?.subscriptionPreferenceSettings?.enabled,
      disclaimer: !!settings?.subscriptionPreferenceSettings?.disclaimer && (!(normalizedRegister?.subscriptionPreferenceSettings) || !(normalizedRegister.subscriptionPreferenceSettings && 'disclaimer' in normalizedRegister.subscriptionPreferenceSettings)) ? settings.subscriptionPreferenceSettings.disclaimer : normalizedRegister?.subscriptionPreferenceSettings?.disclaimer,
    },
    emailVerification: !!settings?.emailVerification && (!normalizedRegister || !(normalizedRegister && 'emailVerification' in normalizedRegister)) ? settings.emailVerification : normalizedRegister?.emailVerification,
  }

  return normalizedRegister
}

function getCustomSuccessMessage(presentation, normalizedRegister) {
  let message
  if (!!presentation?.customSuccessMessage && (!normalizedRegister?.customSuccessPage || !(normalizedRegister.customSuccessPage && 'message' in normalizedRegister.customSuccessPage))) {
    message = presentation.customSuccessMessage
  }
  else if (!!presentation?.successMessage && (!normalizedRegister?.customSuccessPage || !(normalizedRegister.customSuccessPage && 'message' in normalizedRegister.customSuccessPage))) {
    message = presentation.successMessage
  }
  else {
    message = normalizedRegister?.customSuccessPage?.message
  }

  return message
}
